import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CrawlJob,
  CrawlJobStatusDTO,
  WithResourceId,
} from '../../../types/knowledge';
import { mergeCrawlJobStatusDto } from '../crawlJobStatusMerge';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import { knowledgeService } from '../../../services/KnowledgeService';

type UseCrawlStatusStreamsOptions = {
  databaseId: string;
  crawlIds: string[];
};

type StreamLifecycle = 'active' | 'stopped';

function mergeLiveStatusIntoCrawl(
  crawl: WithResourceId<CrawlJob>,
  liveStatus: CrawlJobStatusDTO,
): WithResourceId<CrawlJob> {
  return {
    ...crawl,
    status: liveStatus.status ?? crawl.status,
    errorMessage: liveStatus.errorMessage ?? crawl.errorMessage,
    abandoned:
      crawl.abandoned === false
        ? false
        : (liveStatus.abandoned ?? crawl.abandoned),
    maxDepth: liveStatus.maxDepth ?? crawl.maxDepth,
    maxPages: liveStatus.maxPages ?? crawl.maxPages,
    mappedUrlCount: liveStatus.mappedUrlCount ?? crawl.mappedUrlCount,
    mappingDuration: liveStatus.mappingDuration ?? crawl.mappingDuration,
    crawlingDuration: liveStatus.crawlingDuration ?? crawl.crawlingDuration,
    embeddingDuration: liveStatus.embeddingDuration ?? crawl.embeddingDuration,
    startedAt: liveStatus.startedAt ?? crawl.startedAt,
    startedBy: liveStatus.startedBy ?? crawl.startedBy,
  };
}

export function useCrawlStatusStreams({
  databaseId,
  crawlIds,
}: UseCrawlStatusStreamsOptions) {
  const queryClient = useQueryClient();
  const [liveStatusByCrawlId, setLiveStatusByCrawlId] = useState<
    Map<string, CrawlJobStatusDTO>
  >(() => new Map());

  const streamLifecycleByCrawlIdRef = useRef<Map<string, StreamLifecycle>>(
    new Map(),
  );
  const abortControllersByCrawlIdRef = useRef<Map<string, AbortController>>(
    new Map(),
  );
  const isHookDisposedRef = useRef(false);

  const streamCrawlIdsKey = useMemo(
    () => [...crawlIds].sort().join(','),
    [crawlIds],
  );

  const patchCrawlCache = useCallback(
    (crawlId: string, liveStatus: CrawlJobStatusDTO) => {
      queryClient.setQueryData<Array<WithResourceId<CrawlJob>>>(
        knowledgeQueryKeys.crawls(databaseId),
        (current) => {
          if (!current) {
            return current;
          }

          return current.map((crawl) =>
            crawl.id === crawlId
              ? mergeLiveStatusIntoCrawl(crawl, liveStatus)
              : crawl,
          );
        },
      );
    },
    [databaseId, queryClient],
  );

  const handleStreamComplete = useCallback(() => {
    if (isHookDisposedRef.current) {
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: knowledgeQueryKeys.crawls(databaseId),
    });
    invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
  }, [databaseId, queryClient]);

  const startCrawlStatusStream = useCallback(
    (crawlId: string) => {
      abortControllersByCrawlIdRef.current.get(crawlId)?.abort();

      const controller = new AbortController();
      abortControllersByCrawlIdRef.current.set(crawlId, controller);
      streamLifecycleByCrawlIdRef.current.set(crawlId, 'active');

      void knowledgeService
        .streamCrawlJobStatus(
          crawlId,
          {
            onUpdate: (liveStatus) => {
              if (isHookDisposedRef.current) {
                return;
              }

              setLiveStatusByCrawlId((current) => {
                const next = new Map(current);
                next.set(
                  crawlId,
                  mergeCrawlJobStatusDto(next.get(crawlId), liveStatus),
                );
                return next;
              });
              patchCrawlCache(crawlId, liveStatus);
            },
            onComplete: () => handleStreamComplete(),
          },
          controller.signal,
        )
        .catch(() => {
          // Stream failed; manual refresh remains the fallback.
        })
        .finally(() => {
          if (
            abortControllersByCrawlIdRef.current.get(crawlId) !== controller
          ) {
            return;
          }

          abortControllersByCrawlIdRef.current.delete(crawlId);
          streamLifecycleByCrawlIdRef.current.set(crawlId, 'stopped');
        });
    },
    [handleStreamComplete, patchCrawlCache],
  );

  useEffect(() => {
    isHookDisposedRef.current = false;
    const abortControllersByCrawlId = abortControllersByCrawlIdRef.current;
    const streamLifecycleByCrawlId = streamLifecycleByCrawlIdRef.current;

    const activeCrawlIds =
      streamCrawlIdsKey === '' ? [] : streamCrawlIdsKey.split(',');
    const activeCrawlIdSet = new Set(activeCrawlIds);

    for (const crawlId of activeCrawlIds) {
      if (streamLifecycleByCrawlId.get(crawlId) !== 'active') {
        startCrawlStatusStream(crawlId);
      }
    }

    for (const crawlId of abortControllersByCrawlId.keys()) {
      if (!activeCrawlIdSet.has(crawlId)) {
        abortControllersByCrawlId.get(crawlId)?.abort();
        abortControllersByCrawlId.delete(crawlId);
        streamLifecycleByCrawlId.delete(crawlId);
      }
    }

    return () => {
      isHookDisposedRef.current = true;
      for (const controller of abortControllersByCrawlId.values()) {
        controller.abort();
      }
      abortControllersByCrawlId.clear();
      streamLifecycleByCrawlId.clear();
    };
  }, [startCrawlStatusStream, streamCrawlIdsKey]);

  const clearLiveStatusForCrawl = useCallback((crawlId: string) => {
    setLiveStatusByCrawlId((current) => {
      if (!current.has(crawlId)) {
        return current;
      }

      const next = new Map(current);
      next.delete(crawlId);
      return next;
    });
  }, []);

  const isCrawlStatusStreamActive = useCallback((crawlId: string) => {
    return streamLifecycleByCrawlIdRef.current.get(crawlId) === 'active';
  }, []);

  const restartCrawlStatusStreamIfStopped = useCallback(
    (crawlId: string) => {
      if (streamLifecycleByCrawlIdRef.current.get(crawlId) === 'active') {
        return false;
      }

      startCrawlStatusStream(crawlId);
      return true;
    },
    [startCrawlStatusStream],
  );

  const restartActiveCrawlStatusStreams = useCallback(() => {
    const activeCrawlIds =
      streamCrawlIdsKey === '' ? [] : streamCrawlIdsKey.split(',');
    for (const crawlId of activeCrawlIds) {
      startCrawlStatusStream(crawlId);
    }
  }, [startCrawlStatusStream, streamCrawlIdsKey]);

  return {
    liveStatusByCrawlId,
    clearLiveStatusForCrawl,
    isCrawlStatusStreamActive,
    restartCrawlStatusStreamIfStopped,
    restartActiveCrawlStatusStreams,
  };
}
